import React, { useEffect, useMemo, useRef } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";

import CustomImage from "src/app/components/CustomImage/CustomImage";
import Skeleton, { SkeletonText } from "src/app/components/Skeleton/Skeleton";
import { RootState } from "src/redux/reducers";
import TaskPlaceholder from "src/images/placeholder-image.svg";
import ProjectPlaceholder from "src/images/project-placeholder.svg";
import { UniversalSettingsTab } from "../../SettingsModal";
import { TabButton, getTabButtons } from "./getTabButtons";
import { SettingsTab } from "../../enums";
import SidebarTabButton from "./SidebarTabButton";

import "./SettingsSidebar.scss";

const skeletons = Array.from({ length: 2 }).map((_el, i) => (
  <Skeleton key={i} width={217} height={34} borderRadius={6} />
));

interface SettingsSidebarProps {
  data: any;
  tab: UniversalSettingsTab;
  setTab: (tab: UniversalSettingsTab) => void;
  loading: boolean;
}

export default function SettingsSidebar({
  data,
  tab,
  setTab,
  loading,
}: SettingsSidebarProps) {
  const titleRef = useRef<HTMLDivElement>(null);

  const {
    mainReducer: { identity },
  } = useSelector((state: RootState) => state);

  const filteredButtons = useMemo(() => {
    const tabButtons = getTabButtons(identity?.avatarUrl);

    let buttons: TabButton[] = [];

    if (Array.isArray(data?.tabs)) {
      buttons = data.tabs
        .map((t: { name: SettingsTab; type: string }) => {
          const foundTabButton = tabButtons.find((el) => el.value === t.name);
          return foundTabButton;
        })
        .filter(Boolean);
    }

    return buttons;
  }, [data, identity?.avatarUrl]);

  useEffect(() => {
    if (
      filteredButtons.length &&
      !filteredButtons.find((item) => item?.value === tab)
    ) {
      setTab(filteredButtons[0].value);
    }
  }, [filteredButtons]);

  const getPlaceholderImage = () => {
    switch (data?.context?.type) {
      case "wsProject":
        return ProjectPlaceholder;

      case "wsTask":
        return TaskPlaceholder;

      default:
        return "";
    }
  };

  return (
    <div className="settings-sidebar">
      <div className="settings-sidebar__context">
        {loading ? (
          <>
            <Skeleton width={24} height={24} marginRight={8} borderRadius={2} />
            <SkeletonText width={150} height={16} />
          </>
        ) : (
          <>
            {data?.context?.type !== "wsWorkspace" && (
              <CustomImage
                className={classNames("settings-sidebar__context-image", {
                  "settings-sidebar__context-image--round":
                    data?.context?.type === "wsMember",
                })}
                src={data?.context?.image || getPlaceholderImage}
                alt="image"
              />
            )}

            <div
              className="settings-sidebar__context-title"
              data-tooltip-id="settings-sidebar__context-title-tooltip"
              data-tooltip-place="bottom"
              ref={titleRef}
            >
              {data?.context?.name}
            </div>

            {titleRef.current && titleRef.current.clientWidth >= 169 && (
              <Tooltip
                id="settings-sidebar__context-title-tooltip"
                className="react-tooltip__dark-mode"
              >
                {data?.context?.name}
              </Tooltip>
            )}
          </>
        )}
      </div>

      {loading
        ? skeletons
        : filteredButtons.map((button) => (
            <SidebarTabButton
              key={button.value}
              button={button}
              tab={tab}
              setTab={setTab}
            />
          ))}
    </div>
  );
}
